import { useI18n } from '@i18n/useI18n';
import { CalDay, Day, ItemStack, ItemTime, ListItem, ListRow, LogItem, LogRow, MonthRow, NavPoint, NavRow, RegType, ViewSwitch } from './AttendanceCalendar.styled';
import BG_Icon from "@assets/svg/attender_a.svg";
import { CardFocusTitle, GridCard } from '@components/GridCards/Common.styled';
import moment from 'moment';
import { useEffect, useRef, useState } from 'react';
import db from '@utils/db';
import { useStore } from '@store/appStore';
import ChevronL_Icon from "@assets/svg/chevron-left.svg"
import ChevronR_Icon from "@assets/svg/chevron-right.svg"
import Cal_Icon from "@assets/svg/new-cal.svg"
import Day_Icon from "@assets/svg/new-doc.svg"
import Log_Icon from "@assets/svg/match/late.svg"
import toTitleCase, { normalizeName } from '@utils/textTools';
import Tooltip from '@components/Tooltips/Tooltip';
import CardInfo from '@components/GridCards/CardInfo';
import { time } from 'console';
import RainbowLoader from '@components/Loaders/RainbowLoader';
import 'moment/locale/de'
import 'moment/locale/es'
import 'moment/locale/nl'
import 'moment/locale/fi'
import 'moment/locale/sv'
import 'moment/locale/nb'
import 'moment/locale/fr'
import 'moment/locale/it'
import 'moment/locale/pt'
import 'moment/locale/tr'
import 'moment/locale/is'

interface IProps {
  username: string,
  user_id: string,
  updateTime?: any,
}


export const AttendanceCalendar = (props: IProps) => {
  const { client, filterDate, systemSettings, groups, settings, language } = useStore();
  const i18n = useI18n();
  const [loading, setLoading] = useState(false);
  const [calendar, setCalendar] = useState(null);
  const [calendarData, setCalendarData] = useState(null);
  const [day, setDay] = useState(null);
  const [log, setLog] = useState(null);
  const [calendarDate, setCalendarDate] = useState(null);
  const [calendarDay, setCalendarDay] = useState(moment());
  const [calendarHeader, setCalendarHeader] = useState("Date here");
  const [selectedView, setSelectedView] = useState(1);
  const currentFilterMonth = useRef(moment().month());

  useEffect(() => {
    console.log("user id changed", props.user_id)
    GenerateMonth(moment(), true, false)
    SetCalendarHeader(moment())
  }, [props.user_id])

  useEffect(() => {
    // Refresh whatever's currently being viewed in place - reusing the mount
    // effect above would force first=true and snap the user back to today's
    // month, discarding any navigation they'd already done. GenerateDay and
    // GenerateLog both call setCalendarDay(date) themselves, so feeding them
    // the day that's already selected re-fetches without moving the view.
    const refresh = async () => {
      if (!calendarDate) return;
      let freshData = await GenerateMonth(moment(calendarDate), false, true);
      if (selectedView === 2 && calendarDay) {
        let todaysRegi = freshData.filter((reg) => moment(reg.lessondate).format("yyyy-MM-DD") === calendarDay.format("yyyy-MM-DD"));
        GenerateDay(todaysRegi, calendarDay.format("yyyy-MM-DD"));
      } else if (selectedView === 3 && calendarDay) {
        GenerateLog(calendarDay.format("yyyy-MM-DD"));
      }
    };
    refresh();
  }, [props.updateTime])

  useEffect(() => {
    SwitchView(selectedView)
  }, [i18n.language])

  const getLanguage = () => {
    if (i18n.language === 'no') return 'nb'
    else if (i18n.language === 'se') return 'sv'
    else return i18n.language
  }


  const SetCalendarHeader = (date) => {
    let dateMonth = date?.locale(getLanguage()).format("MMMM YYYY")
    if (date == null) dateMonth = moment().locale(getLanguage()).format("MMMM YYYY")
    setCalendarHeader(toTitleCase(dateMonth))
  }

  const getRegistrations = async (from, to) => {
    const registrationsData = await db.Student_Stat_GetDayviewRegistrations(props.user_id, from, to);
    return registrationsData;
  }

  const GenerateMonth = async (month, first, noDate) => {
    //console.log("Generating month: " + month.format("MMMM YYYY"))
    setLoading(true)
    if (!noDate) {
      if (first === true) {
        setCalendarDate(moment().startOf('month'))
      } else {
        setCalendarDate(month)
      }
    }

    currentFilterMonth.current = month.month()
    let today = moment();
    let firstDayOfMonth = moment(month).startOf('month');
    let lastDayOfMonth = moment(month).endOf('month');
    let dayNumber = firstDayOfMonth.isoWeekday()
    // console.log("f: " + firstDayOfMonth.format("DD-MM-YYYY"))
    // console.log("l: " + lastDayOfMonth.format("DD-MM-YYYY"))

    let todate = lastDayOfMonth;
    if (lastDayOfMonth.isAfter(today)) {
      todate = today;
    }
    // No answer (e.g. the app opened without a connection): an empty month instead of a crash - the
    // profile's update check reloads it once the connection is back.
    let calendarData = (await getRegistrations(firstDayOfMonth.format("YYYY-MM-DD"), todate.format("YYYY-MM-DD"))) ?? []
    setCalendarData(calendarData)

    let arr = [];
    for (let i = 1; i < dayNumber; i++) {
      arr.push(<Day key={i + "emptyday"}></Day>)
    }

    for (let i = 1; i <= month.daysInMonth(); i++) {
      let dayRegi = calendarData.filter(d => {
        let date = moment(d.lessondate)
        return date.date() === (i)
      })

      let date = moment(month).date(i);

      if (dayRegi.length >= 1) {
        let regiColor = CheckRegiType(dayRegi)
        arr.push(<Day disable={false} key={i + "day"} typeColor={regiColor} onClick={() => GenerateDay(dayRegi, dayRegi[0].lessondate)}>{i}</Day>)
      } else if (date.isSameOrBefore(today, 'day')) {
        arr.push(<Day disable={false} key={i + "day"} typeColor={0}>{i}</Day>)
      } else {
        arr.push(<Day disable={true} key={i + "day"} typeColor={0}>{i}</Day>)
      }
    }

    setCalendar(arr)
    setTimeout(() => {
      setLoading(false)
    }, 500);
    return calendarData
  }
  const formatTime = (timeObject) => {
    let d = moment(moment().format("YYYY-MM-DD") + "T" + timeObject);
    //let hours = String(timeObject.hours).padStart(2, '0');
    //let minutes = String(timeObject.minutes).padStart(2, '0');
    return d.format("HH:mm");
  }


  const GenerateDay = (registrations, date?) => {
    let headerDay = moment(date).format("DD");
    let headerMonth = moment(date).locale(getLanguage()).format("MMMM");
    let headerYear = moment(date).format("yyyy");

    setCalendarDay(moment(date))
    //console.log(registrations)
    let arr = registrations?.sort((a, b) => {
      if (a.lessonstarttime < b.lessonstarttime) return -1;
      else if (a.lessonstarttime > b.lessonstarttime) return 1;
      return 0;
    }).map((r, index) => {
      return (<ListItem key={"dayitem" + index}>
        <ItemTime typeColor={r.absencetype}>
          <span>{formatTime(r.lessonstarttime) + " - " + formatTime(r.lessonendtime)} {r.absencetype === 3 ? <b>({r.duration / 60}m)</b> : null}</span>
          <span>{r.course_name.length >= 20 ? <Tooltip content={r.course_name}>{normalizeName(r.course_name, 20)}</Tooltip> : r.course_name}</span>
        </ItemTime>
        <ItemStack typeColor={r.absencetype}>
          <span>{GetRegType(r.absencetype)}</span>
          <span>{r.course_displayname.length >= 20 ? <Tooltip content={r.course_displayname}>{normalizeName(r.course_displayname, 20)}</Tooltip> : r.course_displayname}</span>
        </ItemStack>
      </ListItem>)
    })

    if (arr.length === 0) {
      arr.push(<ListItem miss={true} key={"dayitem_missing"}>
        {i18n.t("no-registration")}
      </ListItem>)
    }

    setCalendarDate(moment(date))
    setDay(arr)
    setCalendarHeader(headerDay + " " + toTitleCase(headerMonth) + " " + headerYear)
    setSelectedView(2);
  }

  const CheckRegiType = (day) => {
    let firstType = day[0].absencetype;
    let matchValue = day.every(v => v.absencetype === firstType);

    if (matchValue) {
      return firstType
    } else {
      return 4
    }
  }

  async function SwitchView(view) {
    if (view === 1) {
      SetCalendarHeader(calendarDate)
    } else if (view === 2) {
      let calendarMonth = moment(calendarDate).month()
      if (calendarDay.month() != currentFilterMonth.current) {
        let m = await GenerateMonth(moment(calendarDay), false, true)
        let todaysRegi = m.filter(reg => moment(reg.lessondate).format("yyyy-MM-DD") === calendarDay.format("yyyy-MM-DD"))
        GenerateDay(todaysRegi, calendarDay.format("yyyy-MM-DD"))
      }
      else {
        let todaysRegi = calendarData.filter(reg => moment(reg.lessondate).format("yyyy-MM-DD") === calendarDay.format("yyyy-MM-DD"))
        GenerateDay(todaysRegi, calendarDay.format("yyyy-MM-DD"))
      }
    } else {
      let calendarMonth = moment(calendarDate).month()
      if (calendarMonth != currentFilterMonth.current) {
        GenerateMonth(moment(calendarDate), false, true)
        GenerateLog(calendarDay.format("yyyy-MM-DD"))
      }
      else {
        GenerateMonth(moment(calendarDate), false, true)
        GenerateLog(calendarDay.format("yyyy-MM-DD"))
      }
    }

    setSelectedView(view)
  }

  async function ArrowSwitch(direction) {
    if (selectedView === 1) {
      if (direction === 1) {
        GenerateMonth(moment(calendarDate).add(-1, 'month'), false, true)
        SetCalendarHeader(moment(calendarDate).add(-1, 'month'))
        setCalendarDate(moment(calendarDate).add(-1, 'month'))
      }

      if (direction === 2) {
        GenerateMonth(moment(calendarDate).add(1, 'month'), false, true)
        SetCalendarHeader(moment(calendarDate).add(1, 'month'))
        setCalendarDate(moment(calendarDate).add(1, 'month'))
      }
    }

    if (selectedView === 2) {
      if (direction === 1) {
        let data = calendarData.filter(reg => moment(reg.lessondate).format("yyyy-MM-DD") === moment(calendarDate).add(-1, 'day').format("yyyy-MM-DD"))
        if (moment(calendarDate).add(-1, 'day').month() != moment(calendarDate).month()) {
          let d = await GenerateMonth(moment(calendarDate).add(-1, 'day'), false, false)
          data = d.filter(reg => moment(reg.lessondate).format("yyyy-MM-DD") === moment(calendarDate).add(-1, 'day').format("yyyy-MM-DD"))
        }
        GenerateDay(data, moment(calendarDate).add(-1, 'day').format("yyyy-MM-DD"))
      }

      if (direction === 2) {
        let data = calendarData.filter(reg => moment(reg.lessondate).format("yyyy-MM-DD") === moment(calendarDate).add(1, 'day').format("yyyy-MM-DD"))
        if (moment(calendarDate).add(1, 'day').month() != moment(calendarDate).month()) {
          let d = await GenerateMonth(moment(calendarDate).add(1, 'day'), false, false)
          data = d.filter(reg => moment(reg.lessondate).format("yyyy-MM-DD") === moment(calendarDate).add(1, 'day').format("yyyy-MM-DD"))
        }
        GenerateDay(data, moment(calendarDate).add(1, 'day').format("yyyy-MM-DD"))
      }
    }

    if (selectedView === 3) {
      // console.log("log arrow")
      if (direction === 1) {
        if (moment(calendarDate).add(-1, 'day').month() != moment(calendarDate).month()) {
          GenerateMonth(moment(calendarDate).add(-1, 'day'), false, false)
        }

        GenerateLog(moment(calendarDate).add(-1, 'day').format("yyyy-MM-DD"))
      }

      if (direction === 2) {
        if (moment(calendarDate).add(1, 'day').month() != moment(calendarDate).month()) {
          GenerateMonth(moment(calendarDate).add(1, 'day'), false, false)
        }
        GenerateLog(moment(calendarDate).add(1, 'day').format("yyyy-MM-DD"))
      }
    }
  }

  function getNextDisAllowed() {
    if (selectedView === 1) {
      return moment(calendarDate).month() == moment().month() && moment(calendarDate).year() == moment().year()
    }
    else if (selectedView === 2) {
      return moment(calendarDate).isSameOrAfter(moment(), 'day')
    }
    else if (selectedView === 3) {
      return moment(calendarDate).isSameOrAfter(moment(), 'day')
    }
  }

  const GetDateLog = async (date) => {
    let timezone = systemSettings.find((s) => s.APP_Setting_ID === 7);
    let logData = await db.Student_Stat_GetAbsenceLogStudentDate(props.user_id, date, timezone.value);

    return logData;
  }

  const getType = (registration_method, timestamp?) => {
    if (
      (registration_method === "CHECKIN_BEFORE_LESSON" ||
        registration_method === "CHECKIN_AFTER_LESSON" ||
        registration_method === "CHECKIN_DURING_LESSON_FIXEDLATE_DURINGLATE" ||
        registration_method === "CHECKIN_DURING_LESSON_FIXEDLATE_AFTERLATE" ||
        registration_method === "CHECKIN_DURING_LESSON_NOFIXEDLATE") && timestamp != null) {
      return "checkin";
    }
    else if ((registration_method === "CHECKOUT_BEFORE_LESSON" ||
      registration_method === "CHECKOUT_AFTER_LESSON" ||
      registration_method === "CHECKOUT_DURING_LESSON_FIXEDLATE" ||
      registration_method === "CHECKOUT_DURING_LESSON_NOFIXEDLATE" ||
      registration_method === "CHECKOUT_DURING_LESSON_PARTIAL") && timestamp != null) {
      return "checkout";
    }
    else if ((registration_method === "SICK_SELF_REGISTRATION" ||
      registration_method === "SICK_TEACHER_REGISTRATION" ||
      registration_method === "SICK_ADMIN_REGISTRATION" ||
      registration_method === "SICK_PARENT_REGISTRATION") && timestamp != null) {
      return "sick";
    }
    else if (registration_method === "AUTO_CHECK_OUT" && timestamp != null) {
      return "autocheckout";
    }
    else if ((registration_method === "TEAMS_NO_SHOW" ||
      registration_method === "TEAMS_PARTIALLY_PRESENT" ||
      registration_method === "TEAMS_FULL_ATTENDANCE") && timestamp != null) {
      return "teamsauto";
    }
    else if (registration_method === "APPROVED_HOLIDAY_OFF" && timestamp != null) {
      return "approved";
    }
    else if (timestamp == null) {
      return "none";
    }
    else {
      return "manuel";
    }
  }

  const getTime = (date, starttime) => {
    let datetime = moment(date).format("YYYY-MM-DD") + "T" + starttime;
    return moment(datetime).format("HH:mm:ss");
  }

  const GenerateLog = async (date?) => {
    let headerDay = moment(date).format("DD");
    let headerMonth = moment(date).locale(getLanguage()).format("MMMM");
    let headerYear = moment(date).format("yyyy");

    let logData = await GetDateLog(date);

    logData = logData.sort((a, b) => {
      let timestampa = a.timestamp;
      let timestampb = b.timestamp;
      timestampa = moment(moment(date).format("YYYY-MM-DD") + "T" + a.starttime).toISOString();
      timestampb = moment(moment(date).format("YYYY-MM-DD") + "T" + b.starttime).toISOString();
      if (timestampa < timestampb) return -1;
      else if (timestampa > timestampb) return 1;
      return 0;
    })

    const grouped = logData.reduce((acc, item) => {
      const key = item.date + " " + item.starttime + " " + item.coursedescription; // use your date property name here
      (acc[key] ??= []).push(item);
      return acc;
    }, {});

    let objects = []

    Object.entries(grouped).forEach(([key, value]) => {

      let items = (value as any[]).sort((a, b) => {
        let timestampa = a.timestamp;
        let timestampb = b.timestamp;
        if (timestampa === null) timestampa = moment(moment(date).format("YYYY-MM-DD") + "T" + a.starttime).toISOString();
        if (timestampb === null) timestampb = moment(moment(date).format("YYYY-MM-DD") + "T" + b.starttime).toISOString();
        if (timestampa < timestampb) return 1;
        else if (timestampa > timestampb) return -1;
        return 0;
      })

      items = items.filter(i => i.timestamp != null)

      objects.push({
        time: moment(moment(value[0].date).format("YYYY-MM-DD") + "T" + value[0].starttime).toISOString(), date: value[0].date, starttime: value[0].starttime, endtime: value[0].endtime, coursedescription: value[0].coursedescription, course_displayname: value[0].course_displayname, course_name: value[0].course_name, items: items
      })
    });

    const getDemoUserName = (type, name) => {
      if ((settings.DEMO === true || settings.DEMO === "true") && groups?.includes(client?.StudentGroupName) && props.user_id == "60249") {
        return type === "checkin" || type === "checkout" ? "Demo student" : "Demo teacher"
      }
      else if ((settings.DEMO === true || settings.DEMO === "true") && !groups?.includes(client?.StudentGroupName) && props.user_id == "60249") {
        return type === "checkin" || type === "checkout" ? "Aaron Christoffersn" : "Demo teacher"
      }
      else {
        return name
      }
    }

    // console.log(groupedLog);


    let arr = []
    let d = moment(date);

    arr = objects?.map((r, index) => {
      // console.log(r)
      // console.log(moment(r.time).format("DD-MM-yyyy") === calendarDay.format("DD-MM-yyyy"))      


      return (
        <LogItem key={"day_log_item" + index}>
          <b>{r.course_name} - {r.coursedescription} - {r.starttime.substring(0, 5) + " - "}{r.endtime.substring(0, 5)}</b>
          {
            r.items.length > 0?
            r.items?.map(e => {
              let otherDay = false;
              if (moment(r.date).format("DD-MM-yyyy") !== moment(e.timestamp).format("DD-MM-yyyy")) {
                otherDay = true
              }

              return (<div style={{color:"#4d4d4d"}}>{e.timestamp ? moment(e.timestamp).format(otherDay ? "DD-MM-yyyy HH:mm:ss" : "HH:mm:ss") : ""} - <RegType typeColor={e.type}> {GetRegType(e.type)} {e.type === 3 ? <b>({e.absence_duration / 60}m)</b> : null}</RegType> - {toTitleCase(i18n.t(getType(e.registration_method, e.timestamp)))} {r.name !== "none" ? "- " + getDemoUserName(r.name, e.teacher_displayname) : null}</div>)
            }):
            <div style={{color:"#4d4d4d"}}>{i18n.t('no-registration')}</div>
          }
        </LogItem>
      )
    })

    console.log(objects)

    if (arr.length === 0) {
      arr.push(<ListItem miss={true} key={"dayitem_missing"}>
        {i18n.t("no-events")}
      </ListItem>)
    }

    setLog(arr)

    setCalendarDay(moment(date))
    setCalendarDate(moment(date))
    setCalendarHeader(headerDay + " " + toTitleCase(headerMonth) + " " + headerYear)
    setSelectedView(3);
  }

  const GetRegType = (regType) => {
    if (regType === 1 || regType == -1) return i18n.t("attendance")
    else if (regType === 2) return i18n.t("absence")
    else if (regType === 3) return i18n.t("late")
  }

  const getTitle = (view) => {
    if(view === 1)return 'cal-title'
    else if(view === 2)return 'cal-view-day'
    else if(view === 3)return 'cal-view-log'
  }

  return (
    <GridCard col={1}>
      <CardInfo padding={true} text="cal-tooltip" />
      <CardFocusTitle>{i18n.t(getTitle(selectedView))}</CardFocusTitle>

      <ViewSwitch hideNextMonth={getNextDisAllowed()}>
        <div onClick={() => loading === true ? null : ArrowSwitch(1)}><ChevronL_Icon /></div>
        <span>{calendarHeader}</span>
        <div onClick={() => loading === true ? null : ArrowSwitch(2)}><ChevronR_Icon /></div>
      </ViewSwitch>


      {loading === true ? <RainbowLoader /> : selectedView === 1 ?
        <MonthRow>
          <CalDay>{moment().locale(getLanguage()).startOf('week').isoWeekday(1).format('dddd').substring(0, 1).toUpperCase()}</CalDay>
          <CalDay>{moment().locale(getLanguage()).startOf('week').isoWeekday(2).format('dddd').substring(0, 1).toUpperCase()}</CalDay>
          <CalDay>{moment().locale(getLanguage()).startOf('week').isoWeekday(3).format('dddd').substring(0, 1).toUpperCase()}</CalDay>
          <CalDay>{moment().locale(getLanguage()).startOf('week').isoWeekday(4).format('dddd').substring(0, 1).toUpperCase()}</CalDay>
          <CalDay>{moment().locale(getLanguage()).startOf('week').isoWeekday(5).format('dddd').substring(0, 1).toUpperCase()}</CalDay>
          <CalDay>{moment().locale(getLanguage()).startOf('week').isoWeekday(6).format('dddd').substring(0, 1).toUpperCase()}</CalDay>
          <CalDay>{moment().locale(getLanguage()).startOf('week').isoWeekday(0).format('dddd').substring(0, 1).toUpperCase()}</CalDay>

          {calendar}
        </MonthRow>

        : selectedView === 2 ?
          <ListRow>
            {day}
          </ListRow>

          :
          <LogRow>
            {log}
          </LogRow>
      }

      <NavRow>
        <Tooltip content={i18n.t("cal-view-month")}><NavPoint onClick={() => SwitchView(1)} active={selectedView === 1}><Cal_Icon /></NavPoint></Tooltip>
        <Tooltip content={i18n.t("cal-view-day")}><NavPoint onClick={() => SwitchView(2)} active={selectedView === 2}><Day_Icon /></NavPoint></Tooltip>
        <Tooltip content={i18n.t("cal-view-log")}><NavPoint onClick={() => SwitchView(3)} active={selectedView === 3}><Log_Icon /></NavPoint></Tooltip>
      </NavRow>
    </GridCard>
  );
};

export default AttendanceCalendar;
